import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Local } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface LocalState {
  locals: Local[];
  local: Local | null;
  activeLocalId: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: LocalState = {
  locals: [],
  local: null,
  activeLocalId: null,
  loading: false,
  error: null,
};

const safeAppend = (formData: FormData, key: string, value: any) => {
  if (value !== undefined && value !== null && value !== '') {
    formData.append(key, value);
  }
};

export const fetchLocals = createAsyncThunk<Local[], void, { state: RootState }>(
  'local/fetchLocals',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const ownerId = authentification.user.OwnerId;
      console.log('🔍 fetchLocals - OwnerId:', ownerId);
      
      const response = await axios.get(`${baseUrlApi}/locals/all/${ownerId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      
      console.log('✅ Locaux récupérés:', response.data.length);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur fetchLocals:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch locals');
    }
  }
);

export const fetchLocal = createAsyncThunk<Local, number, { state: RootState }>(
  'local/fetchLocal',
  async (localId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      console.log('🔍 fetchLocal - ID:', localId);
      
      const response = await axios.get(`${baseUrlApi}/locals/${localId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      
      dispatch(fetchLocals());
      console.log('✅ Local récupéré:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur fetchLocal:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch local');
    }
  }
);

export const createLocal = createAsyncThunk<Local, FormData | any, { state: RootState }>(
  'local/createLocal',
  async (localData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const token = authentification.user.token;
      const ownerId = authentification.user.OwnerId;

      console.log('🔍 createLocal - Type de données:', localData instanceof FormData ? 'FormData' : 'Object');
      console.log('🔍 OwnerId depuis Redux:', ownerId);

      // ✅ Si localData est déjà un FormData (depuis le composant), on l'utilise directement
      let formData: FormData;
      
      if (localData instanceof FormData) {
        console.log('✅ Utilisation du FormData existant');
        formData = localData;
        
        // ✅ S'assurer que OwnerId est présent et correct
        if (!formData.has('OwnerId') || formData.get('OwnerId') === 'undefined') {
          if (formData.has('OwnerId')) {
            formData.delete('OwnerId');
          }
          formData.append('OwnerId', String(ownerId));
          console.log('✅ OwnerId ajouté/corrigé dans FormData:', ownerId);
        }

        // ✅ Debug: Afficher le contenu du FormData
        console.log('📦 Contenu FormData:');
        for (let [key, value] of formData.entries()) {
          console.log(`   ${key}:`, value instanceof File ? `File(${value.name})` : value);
        }
      } else {
        // ✅ Sinon on crée un nouveau FormData (cas legacy)
        console.log('🆕 Création nouveau FormData');
        formData = new FormData();
        safeAppend(formData, "name", localData.name);
        safeAppend(formData, "local_type", localData.local_type);
        safeAppend(formData, "status", localData.status || 'active');
        safeAppend(formData, "street", localData.street);
        safeAppend(formData, "street2", localData.street2);
        safeAppend(formData, "city", localData.city);
        safeAppend(formData, "zip", localData.zip);
        safeAppend(formData, "description", localData.description);
        safeAppend(formData, "Numwater", localData.Numwater);
        safeAppend(formData, "Numedf", localData.Numedf);
        safeAppend(formData, "Numgaz", localData.Numgaz);
        safeAppend(formData, "image", localData.image);
        safeAppend(formData, "OwnerId", String(ownerId));
      }
      
      const response = await axios.post(`${baseUrlApi}/locals/`, formData, {
        headers: {
          Authorization: `bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      console.log('✅ Local créé avec succès:', response.data);
      dispatch(fetchLocals());
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur createLocal:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to create local');
    }
  }
);

export const updateLocal = createAsyncThunk<
  Local,
  { localData: FormData | any; localId: number },
  { state: RootState }
>(
  'local/updateLocal',
  async ({ localData, localId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const token = authentification.user.token;
      const ownerId = authentification.user.OwnerId;

      console.log('🔍 updateLocal - ID:', localId);
      console.log('🔍 Type de données:', localData instanceof FormData ? 'FormData' : 'Object');

      // ✅ Si FormData, s'assurer que OwnerId est présent
      if (localData instanceof FormData) {
        if (!localData.has('OwnerId') || localData.get('OwnerId') === 'undefined') {
          if (localData.has('OwnerId')) {
            localData.delete('OwnerId');
          }
          localData.append('OwnerId', String(ownerId));
          console.log('✅ OwnerId ajouté dans FormData pour update:', ownerId);
        }

        // ✅ Debug: Afficher le contenu du FormData
        console.log('📦 Contenu FormData pour update:');
        for (let [key, value] of localData.entries()) {
          console.log(`   ${key}:`, value instanceof File ? `File(${value.name})` : value);
        }
      }

      const response = await axios.put(`${baseUrlApi}/locals/${localId}`, localData, {
        headers: { 
          Authorization: `bearer ${token}`,
          ...(localData instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {})
        }
      });

      // ✅ Gérer l'image séparément si présente (pour compatibilité backend)
      if (localData instanceof FormData && localData.has('image')) {
        const imageFile = localData.get('image');
        if (imageFile instanceof File) {
          const photoFormData = new FormData();
          photoFormData.append("photos", imageFile);

          await axios.post(`${baseUrlApi}/locals/${localId}/pictures/`, photoFormData, {
            headers: {
              Authorization: `bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            }
          });
          console.log('✅ Image uploadée séparément');
        }
      }

      console.log('✅ Local mis à jour avec succès');
      dispatch(fetchLocals());
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur updateLocal:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to update local');
    }
  }
);

export const deleteLocal = createAsyncThunk<number, number, { state: RootState }>(
  'local/deleteLocal',
  async (localId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      console.log('🗑️ Suppression local:', localId);
      
      await axios.delete(`${baseUrlApi}/locals/${localId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      
      dispatch(fetchLocals());
      console.log('✅ Local supprimé');
      return localId;
    } catch (error: any) {
      console.error('❌ Erreur deleteLocal:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to delete local');
    }
  }
);

const localSlice = createSlice({
  name: 'local',
  initialState,
  reducers: {
    clearLocal: (state) => { 
      state.local = null; 
      console.log('🧹 Local effacé');
    },
    clearLocals: (state) => { 
      state.locals = []; 
      console.log('🧹 Tous les locaux effacés');
    },
    resetLocalState: () => {
      console.log('🔄 Reset état local');
      return initialState;
    },
    encoursLocal: (state, action: PayloadAction<number>) => {
      state.activeLocalId = action.payload;
      console.log('✅ Local actif défini:', action.payload);
    },
  },
  extraReducers: (builder) => {
    [fetchLocals, fetchLocal, createLocal, updateLocal, deleteLocal].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    builder
      .addCase(fetchLocals.fulfilled, (state, action) => {
        state.loading = false;
        state.locals = action.payload;
      })
      .addCase(fetchLocal.fulfilled, (state, action) => {
        state.loading = false;
        state.local = action.payload;
      })
      .addCase(createLocal.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateLocal.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteLocal.fulfilled, (state, action) => {
        state.loading = false;
        state.locals = state.locals.filter(l => l.id !== action.payload);
      });
  },
});

export const { clearLocal, clearLocals, resetLocalState, encoursLocal } = localSlice.actions;
export default localSlice.reducer;